export const publicationRequirementCodes = [
    "LOCALE_MISMATCH",
    "VERSION_MISMATCH",
    "CONTENT_HASH_MISMATCH",
    "NO_SOURCES",
    "SOURCE_RECORD_MISSING",
    "SOURCE_IDENTIFIER_MISSING",
    "SOURCE_METADATA_INCOMPLETE",
    "SOURCE_STALE",
    "GUIDELINE_RECORD_MISSING",
    "JURISDICTION_MISSING",
    "EVIDENCE_NOTES_MISSING",
    "AUTHOR_MISSING",
    "AUTHOR_INACTIVE",
    "REVIEWER_MISSING",
    "REVIEWER_INACTIVE",
    "REVIEW_DATE_MISSING",
    "REVIEW_DECISION_MISSING",
    "REVIEW_DECISION_MISMATCH",
    "REVIEW_NOT_APPROVED",
    "REVIEW_STALE",
    "STATE_NOT_VERIFIED",
    "SUPERSEDED",
    "RETIRED",
];
export const defaultPublicationPolicy = {
    maximumReviewAgeDays: 365,
    maximumSourceRetrievalAgeDays: 730,
};
function dateFreshness(dates, now, maximumAgeDays) {
    if (dates.length === 0 ||
        dates.some((date) => date === undefined || Number.isNaN(Date.parse(date)))) {
        return "UNKNOWN";
    }
    const maximumAge = maximumAgeDays * 24 * 60 * 60 * 1000;
    return dates.some((date) => now - Date.parse(date ?? "") > maximumAge) ? "STALE" : "CURRENT";
}
export function assessPublicationEligibility(context, now, policy = defaultPublicationPolicy) {
    const missing = new Set();
    const { revision, editorial } = context;
    if (editorial.locale !== revision.locale)
        missing.add("LOCALE_MISMATCH");
    if (editorial.revisionId !== revision.id)
        missing.add("VERSION_MISMATCH");
    if (editorial.contentHash !== revision.contentHash)
        missing.add("CONTENT_HASH_MISMATCH");
    if (revision.sourceIds.length === 0)
        missing.add("NO_SOURCES");
    if (revision.jurisdictionId.trim().length === 0)
        missing.add("JURISDICTION_MISSING");
    if (revision.evidenceNotes.trim().length === 0)
        missing.add("EVIDENCE_NOTES_MISSING");
    const sources = revision.sourceIds.map((id) => context.sources.find((source) => source.id === id));
    if (sources.some((source) => source === undefined))
        missing.add("SOURCE_RECORD_MISSING");
    for (const source of sources) {
        if (source === undefined)
            continue;
        if (source.sourceUrl === undefined && source.documentIdentifier === undefined) {
            missing.add("SOURCE_IDENTIFIER_MISSING");
        }
        if (source.publisher.trim().length === 0 ||
            source.evidenceNotes.trim().length === 0 ||
            (source.publishedAt === undefined && source.revisedAt === undefined)) {
            missing.add("SOURCE_METADATA_INCOMPLETE");
        }
    }
    if (revision.guidelineIds.some((id) => !context.guidelines.some((guideline) => guideline.id === id))) {
        missing.add("GUIDELINE_RECORD_MISSING");
    }
    const author = context.reviewers.find((reviewer) => reviewer.id === revision.authorId);
    if (author === undefined)
        missing.add("AUTHOR_MISSING");
    else if (!author.active)
        missing.add("AUTHOR_INACTIVE");
    const reviewer = context.reviewers.find((identity) => identity.id === editorial.reviewerId);
    if (editorial.reviewerId === undefined || reviewer === undefined)
        missing.add("REVIEWER_MISSING");
    else if (!reviewer.active)
        missing.add("REVIEWER_INACTIVE");
    if (editorial.reviewedAt === undefined)
        missing.add("REVIEW_DATE_MISSING");
    const decision = context.reviewDecisions.find((candidate) => candidate.id === editorial.reviewDecisionId);
    if (decision === undefined)
        missing.add("REVIEW_DECISION_MISSING");
    else {
        if (decision.revisionId !== revision.id ||
            decision.locale !== revision.locale ||
            decision.contentHash !== revision.contentHash ||
            decision.reviewerId !== editorial.reviewerId) {
            missing.add("REVIEW_DECISION_MISMATCH");
        }
        if (decision.decision !== "APPROVE")
            missing.add("REVIEW_NOT_APPROVED");
    }
    if (editorial.state !== "VERIFIED" && editorial.state !== "PUBLISHED") {
        missing.add("STATE_NOT_VERIFIED");
    }
    const disposition = context.dispositions.find((record) => record.revisionId === revision.id && record.locale === revision.locale);
    if (disposition?.action === "SUPERSEDED")
        missing.add("SUPERSEDED");
    if (disposition?.action === "RETIRED")
        missing.add("RETIRED");
    const sourceFreshness = dateFreshness(sources.map((source) => source?.retrievedAt), now, policy.maximumSourceRetrievalAgeDays);
    if (sourceFreshness === "STALE")
        missing.add("SOURCE_STALE");
    const reviewFreshness = dateFreshness([editorial.reviewedAt], now, policy.maximumReviewAgeDays);
    if (reviewFreshness === "STALE")
        missing.add("REVIEW_STALE");
    return {
        eligible: missing.size === 0,
        missingRequirements: [...missing],
        reviewFreshness,
        sourceFreshness,
    };
}
